import React, { useMemo, useState } from "react";

/**
 * LawspeakerPanel — Renders the verdict from the Lawspeaker compliance gate.
 *
 * Renders a traffic-light verdict per platform, with collapsible issue lists,
 * jurisdiction verdicts, and an overall score. "warn" verdicts can be
 * overridden via the "Override & proceed" button (audit-logged in parent).
 * "block" verdicts cannot be overridden in v1.
 *
 * Verdict shape (matches @lawspeaker/schemas /verdict-response):
 * {
 *   verdict: "pass" | "warn" | "block",
 *   score: 0..1,
 *   policy_snapshot: { db_version, last_sync },
 *   platforms: { <key>: { verdict, issues: [{ severity, code, message, policy_url, verified_at }] } },
 *   jurisdictions: { <key>: { verdict, reason } },
 *   recommendations: string[],
 *   audit_id: string,
 * }
 *
 * @param {{
 *   verdict: object | null,
 *   loading: boolean,
 *   error: string | null,
 *   onOverride: (auditEntry: { platform: string, reason: string }) => void,
 *   onResync: () => void,
 * }} props
 */

const PLATFORM_LABELS = {
  google: { label: "Google Ads / YouTube", icon: "G" },
  meta: { label: "Meta (FB/IG/Threads)", icon: "M" },
  tiktok: { label: "TikTok", icon: "T" },
  amazon: { label: "Amazon Ads / DSP", icon: "A" },
  openai: { label: "OpenAI ChatGPT Ads", icon: "AI" },
  dv360: { label: "DV360", icon: "DV" },
  gam: { label: "Google Ad Manager", icon: "GAM" },
  simplifi: { label: "Simpli.fi", icon: "Si" },
};

function VerdictDot({ verdict, size = 12 }) {
  // Monochrome traffic light: brightness encodes status
  const color = verdict === "block" ? "#5a5a5a" : verdict === "warn" ? "#b0b0b0" : "#ededed";
  return (
    <span
      className="verdict-dot"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        marginRight: 6,
        verticalAlign: "middle",
      }}
      aria-label={`verdict: ${verdict}`}
    />
  );
}

function VerdictBanner({ verdict, score, recommendations = [] }) {
  if (verdict === "pass") {
    return (
      <div className="compliance-banner compliance-pass">
        <VerdictDot verdict="pass" size={16} />
        <strong>Compliance check passed</strong>
        <span className="compliance-score">Score: {(score * 100).toFixed(0)}%</span>
      </div>
    );
  }

  if (verdict === "block") {
    return (
      <div className="compliance-banner compliance-block">
        <VerdictDot verdict="block" size={16} />
        <strong>Campaign is blocked</strong>
        <span className="compliance-score">Score: {(score * 100).toFixed(0)}%</span>
        {recommendations.length > 0 && (
          <ul className="compliance-recommendations">
            {recommendations.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // warn
  return (
    <div className="compliance-banner compliance-warn">
      <VerdictDot verdict="warn" size={16} />
      <strong>Warnings found - review before launch</strong>
      <span className="compliance-score">Score: {(score * 100).toFixed(0)}%</span>
      {recommendations.length > 0 && (
        <ul className="compliance-recommendations">
          {recommendations.map((rec, i) => (
            <li key={i}>{rec}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IssueRow({ issue, onOverride, overrideActive, onCancelOverride, overrideReason, setOverrideReason }) {
  const [expanded, setExpanded] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  const severityClass = `issue-severity-${issue.severity}`;

  return (
    <div className={`compliance-issue ${severityClass}`}>
      <div className="compliance-issue-header" onClick={() => setExpanded((e) => !e)}>
        <span className={`issue-badge issue-${issue.severity}`}>{issue.severity}</span>
        <span className="issue-code">{issue.code}</span>
        <span className="issue-message">{issue.message}</span>
        <button className="btn-icon" aria-label={expanded ? "collapse" : "expand"}>
          {expanded ? "▾" : "▸"}
        </button>
      </div>
      {expanded && (
        <div className="compliance-issue-body">
          {issue.policy_url && (
            <div className="issue-policy">
              Policy: <a href={issue.policy_url} target="_blank" rel="noopener noreferrer">{issue.policy_url}</a>
            </div>
          )}
          <div className="issue-verified">
            Verified: {new Date(issue.verified_at).toLocaleString()}
          </div>
          {issue.confidence != null && (
            <div className="issue-confidence">
              Confidence: {(issue.confidence * 100).toFixed(0)}%
            </div>
          )}

          {overrideActive ? (
            <div className="override-confirmed">
              <VerdictDot verdict="pass" size={10} />
              Override applied — campaign may proceed despite this warning.
            </div>
          ) : issue.severity === "warning" && onOverride ? (
            showOverride ? (
              <div className="override-form">
                <textarea
                  rows={2}
                  placeholder="Reason for override (required for audit log)…"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                />
                <div className="override-form-actions">
                  <button
                    className="btn btn-sm"
                    onClick={() => {
                      if (overrideReason.trim()) {
                        onOverride(overrideReason);
                        setShowOverride(false);
                      }
                    }}
                    disabled={!overrideReason.trim()}
                  >
                    Confirm override
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => {
                      setShowOverride(false);
                      setOverrideReason("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button className="btn btn-sm btn-warn" onClick={() => setShowOverride(true)}>
                Override & proceed
              </button>
            )
          ) : null}

          {issue.severity === "block" && (
            <div className="override-blocked-notice">
              Block-level issues cannot be overridden. Campaign must be modified.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlatformCard({ platformKey, platformVerdict, onOverride, overrides }) {
  const [open, setOpen] = useState(platformVerdict.verdict !== "pass");
  const meta = PLATFORM_LABELS[platformKey] || { label: platformKey, icon: platformKey[0]?.toUpperCase() };
  const issueCount = (platformVerdict.issues || []).length;

  return (
    <div className={`compliance-platform compliance-platform-${platformVerdict.verdict}`}>
      <div className="compliance-platform-header" onClick={() => setOpen((o) => !o)}>
        <span className="platform-icon">{meta.icon}</span>
        <span className="platform-name">{meta.label}</span>
        <VerdictDot verdict={platformVerdict.verdict} />
        <span className={`verdict-label verdict-label-${platformVerdict.verdict}`}>
          {platformVerdict.verdict}
        </span>
        {issueCount > 0 && <span className="issue-count">({issueCount})</span>}
        <button className="btn-icon" aria-label={open ? "collapse" : "expand"}>{open ? "▾" : "▸"}</button>
      </div>
      {open && issueCount > 0 && (
        <div className="compliance-platform-issues">
          {platformVerdict.issues.map((issue, idx) => {
            const overrideKey = `${platformKey}-${issue.code}-${idx}`;
            return (
              <IssueRowWithState
                key={overrideKey}
                issue={issue}
                platformKey={platformKey}
                onOverride={onOverride}
                overrideActive={overrides.has(overrideKey)}
              />
            );
          })}
        </div>
      )}
      {open && issueCount === 0 && (
        <div className="compliance-platform-issues">
          <div className="compliance-no-issues">No policy concerns detected.</div>
        </div>
      )}
    </div>
  );
}

function IssueRowWithState({ issue, platformKey, onOverride, overrideActive }) {
  const [overrideReason, setOverrideReason] = useState("");
  return (
    <IssueRow
      issue={issue}
      overrideActive={overrideActive}
      onOverride={(reason) => onOverride(platformKey, issue.code, reason)}
      overrideReason={overrideReason}
      setOverrideReason={setOverrideReason}
    />
  );
}

function JurisdictionCard({ jurKey, jurVerdict }) {
  return (
    <div className={`compliance-jurisdiction compliance-jurisdiction-${jurVerdict.verdict}`}>
      <div className="compliance-jurisdiction-header">
        <VerdictDot verdict={jurVerdict.verdict} />
        <span className="jurisdiction-name">{jurKey.replace(/_/g, " ")}</span>
        <span className={`verdict-label verdict-label-${jurVerdict.verdict}`}>{jurVerdict.verdict}</span>
      </div>
      {jurVerdict.reason && (
        <div className="compliance-jurisdiction-reason">{jurVerdict.reason}</div>
      )}
    </div>
  );
}

export default function LawspeakerPanel({ verdict, loading, error, onOverride, overrides = new Set(), lastChecked }) {
  if (loading) {
    return (
      <div className="lawspeaker-panel">
        <div className="compliance-loading">Checking Lawspeaker compliance gate…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lawspeaker-panel">
        <div className="compliance-error">
          <strong>Lawspeaker service unavailable</strong>
          <p>{error}</p>
          <p className="compliance-hint">
            Manual review is required when Lawspeaker cannot be reached. Campaign can still be approved;
            ensure compliance is confirmed by a trafficker.
          </p>
        </div>
      </div>
    );
  }

  if (!verdict) {
    return (
      <div className="lawspeaker-panel">
        <div className="compliance-empty">
          No compliance verdict available. Run a check to validate this campaign.
        </div>
      </div>
    );
  }

  const platformKeys = Object.keys(verdict.platforms || {});
  const jurisdictionKeys = Object.keys(verdict.jurisdictions || {});

  return (
    <div className="lawspeaker-panel">
      <VerdictBanner
        verdict={verdict.verdict}
        score={verdict.score}
        recommendations={verdict.recommendations || []}
      />

      <div className="compliance-meta">
        <span className="compliance-policy-version">
          Policy DB v{verdict.policy_snapshot?.db_version ?? "—"}
        </span>
        {verdict.policy_snapshot?.last_sync && (
          <span className="compliance-policy-sync">
            Last synced: {new Date(verdict.policy_snapshot.last_sync).toLocaleString()}
          </span>
        )}
        <span className="compliance-audit-id mono">{verdict.audit_id}</span>
      </div>

      {platformKeys.length > 0 && (
        <div className="compliance-platforms">
          <h5>Platform verdicts</h5>
          {platformKeys.map((key) => (
            <PlatformCard
              key={key}
              platformKey={key}
              platformVerdict={verdict.platforms[key]}
              onOverride={onOverride}
              overrides={overrides}
            />
          ))}
        </div>
      )}

      {jurisdictionKeys.length > 0 && (
        <div className="compliance-jurisdictions">
          <h5>Jurisdiction verdicts</h5>
          {jurisdictionKeys.map((key) => (
            <JurisdictionCard key={key} jurKey={key} jurVerdict={verdict.jurisdictions[key]} />
          ))}
        </div>
      )}

      {lastChecked && (
        <div className="compliance-last-checked">
          Last checked: {lastChecked}
        </div>
      )}
    </div>
  );
}